import React, { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Divider, Paper, Stack, useTheme } from '@mui/material';
import { useToggle } from 'usehooks-ts';

import { LocationUpdateDTO, PostTalentsResponse, useCreateTalent, useGetOrganizationStructure } from '@/api/generated';
import ConfirmationModal from '@/components/ConfirmationModal/ConfirmationModal';
import { buildMapStructure, buildRecursiveStructure } from '@/components/fields/Location/locationTreeUtils';
import LoadingButton from '@/components/LoadingButton';
import { useClearLocationState } from '@/hooks/useClearLocationState';
import { useSnackbar } from '@/hooks/useSnackbar';
import { toTalent } from '@/root/linker';
import AddToProcesses from '@/views/TalentEditInfoModal/AddToProcess';

import { TalentCreateFormValues } from './types';

const TalentAddToProcessView = ({
  onGoBack,
  onGoNext
}: {
  onGoBack(): void;
  onGoNext(talent: PostTalentsResponse): void;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { clearState } = useClearLocationState();
  const [showCreateConfirmation, toggleShowCreateConfirmation] = useToggle(false);
  const { showMessage } = useSnackbar();
  const { mutateAsync: createTalent, isLoading: isSavingTalent } = useCreateTalent();
  const formMethods = useFormContext<TalentCreateFormValues>();
  const organizationSlug = formMethods.watch('organizationSlug');
  const selectedProcesses = formMethods.watch('selectedProcesses');
  const { data: organizationStructure } = useGetOrganizationStructure(organizationSlug!, {
    query: {
      enabled: !!organizationSlug
    }
  });

  const recursiveStructure = useMemo(
    () => organizationStructure && buildRecursiveStructure(organizationStructure),
    [organizationStructure]
  );

  const mapStructure = useMemo(
    () => (recursiveStructure ? buildMapStructure(recursiveStructure) : {}),
    [recursiveStructure]
  );
  const { spacing, palette } = useTheme();

  const handleCreateTalent = useCallback(async () => {
    const { selectedProcesses, locations: locationSlugs, ...talentData } = formMethods.getValues();
    const processes = selectedProcesses?.size ? Array.from(selectedProcesses?.keys()) : [];

    const locations: LocationUpdateDTO[] = (locationSlugs || [])
      .filter((locationSlug: string) => mapStructure[locationSlug])
      .map(
        locationSlug =>
          ({
            slug: locationSlug,
            type: mapStructure[locationSlug]!.type
          }) satisfies LocationUpdateDTO
      );

    try {
      const data = await createTalent({
        organizationSlug: organizationSlug!,
        data: { ...talentData, processes, locations }
      });

      return data;
    } catch (e) {
      showMessage(t('common:error.somethingWentWrong'));
      throw e;
    }
  }, [createTalent, formMethods, mapStructure, organizationSlug, showMessage, t]);

  const handleCreateSubmit = useCallback(async () => {
    if (!selectedProcesses?.size) {
      toggleShowCreateConfirmation();

      return;
    }

    const createdTalent = await handleCreateTalent();
    clearState();

    if (selectedProcesses.size) {
      onGoNext(createdTalent);
    }
  }, [handleCreateTalent, clearState, onGoNext, selectedProcesses?.size, toggleShowCreateConfirmation]);

  const handleIgnoreConfirmation = useCallback(async () => {
    toggleShowCreateConfirmation();
    const createdTalent = await handleCreateTalent();
    clearState();
    navigate(toTalent({ talentId: createdTalent.id! }));
  }, [handleCreateTalent, navigate, clearState, toggleShowCreateConfirmation]);

  return (
    <>
      <Paper
        elevation={0}
        sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, backgroundColor: 'background.default' }}
      >
        <Box flexBasis={0} flexGrow={1} overflow="auto">
          {organizationSlug && (
            <AddToProcesses
              onSubmit={formMethods.handleSubmit(handleCreateSubmit)}
              draftTalent={{ type: formMethods.watch('type')! }}
              organizationSlug={organizationSlug}
              outerFormContext={formMethods}
              footer={
                <>
                  <Divider sx={{ marginTop: 'auto', borderColor: palette.grey[400] }} />
                  <Stack
                    direction="row"
                    sx={{ padding: spacing(2.5, 2, 2), gap: spacing(1.5), justifyContent: 'space-between' }}
                  >
                    <Button onClick={onGoBack}>{t('common:actions.back')}</Button>
                    <LoadingButton type="submit" color="primary" variant="contained" isLoading={isSavingTalent}>
                      {t('common:actions.createAndContinue')}
                    </LoadingButton>
                  </Stack>
                </>
              }
            />
          )}
        </Box>
      </Paper>
      <ConfirmationModal
        isBlockingModal
        open={showCreateConfirmation}
        title={t('talents:layouts.talents.createConfirmation')}
        confirmButtonLabel={t('talents:layouts.talentEditModal.tabs.addToProcess')}
        discardButtonLabel={t('common:actions.ignore')}
        loadingLabel={t('processes:layouts.process.menu.removeFromProcess.removing')}
        onConfirm={toggleShowCreateConfirmation}
        onDiscard={handleIgnoreConfirmation}
      />
    </>
  );
};

export default TalentAddToProcessView;
